import React, { useState, useEffect, useMemo } from 'react';
import {
  Table, message, Tooltip, Button, Modal, Form,
  InputNumber, Input } from 'antd';
import PujaSpinner from '../../components/PujaSpinner';
import {
  ArrowUpOutlined, ArrowDownOutlined,
  DollarOutlined, SearchOutlined,
} from '@ant-design/icons';
import { buyerService, ofertaService } from '../../services/api';
import { urlMicrositio } from '../../utils/urlMicrositio';
import { formatearMonto, parsearMonto } from '../../utils/montoInput';
import { minimoOfertable, enEscalon, escalonSiguiente } from '../../utils/escalonOferta';
import { htmlSanitizado } from '../../utils/htmlSeguro';
import dayjs from 'dayjs';

// `slugify` / `buildAnimalSlug` locales borrados: el link al animal lo arma
// `urlMicrositio()`, que ademas respeta el micrositio incrustado. Eran una de
// las copias del slugify que andaban dando vueltas por el front.

const MisOfertas = () => {
  const [loading, setLoading] = useState(true);
  const [ofertas, setOfertas] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [ofertaActiva, setOfertaActiva] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Condiciones del remate: se aceptan una sola vez por evento, y el backend
  // las exige recién al ofertar. `montoPendiente` guarda la oferta que quedó
  // trunca para reintentarla sola después de aceptar.
  const [condicionesVisible, setCondicionesVisible] = useState(false);
  const [condiciones, setCondiciones] = useState(null);
  const [aceptando, setAceptando] = useState(false);
  const [montoPendiente, setMontoPendiente] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchOfertas();
  }, []);

  const fetchOfertas = async () => {
    setLoading(true);
    try {
      const res = await buyerService.getMisOfertas();
      setOfertas(res.data || []);
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al cargar ofertas');
    } finally {
      setLoading(false);
    }
  };

  // Mismas reglas que la ficha del micrositio: el mínimo sale de la mejor
  // oferta del animal más el salto, y se PRECARGA. Antes el modal abría con un
  // input vacío y `min=1`, así que había que adivinar el escalón y comerse un
  // 400 del backend.
  /**
   * Por qué NO se puede ofertar en esta fila, o null si se puede.
   *
   * El backend igual rechaza —las tres barreras viven ahí— pero enterarse
   * después de tipear un monto es la peor forma de saberlo. Se lee
   * `estado_ofertas`, el mismo que decide el candado, así que el botón y el
   * backend no pueden discrepar.
   */
  const motivoNoOfertable = (r) => {
    if (r?.es_finalizado) return 'El remate finalizó';
    const estado = r?.estado_ofertas?.estado;
    if (estado === 'programadas') return 'Las pre-ofertas todavía no abrieron';
    if (estado === 'cerradas')    return 'El período de pre-ofertas cerró';
    if (estado === 'inactivas')   return 'Las pre-ofertas no están habilitadas';
    return null;
  };

  const minimoDe = (r) => minimoOfertable({
    precioBase:  r?.precio_base,
    salto:       r?.salto_oferta,
    mejorOferta: r?.mejor_oferta,
  });

  const abrirModalPreOferta = (record) => {
    setOfertaActiva(record);
    setModalVisible(true);
  };

  // El monto se precarga en un efecto y no en `abrirModalPreOferta`: el Modal
  // tiene `destroyOnHidden`, asi que mientras esta cerrado el <Form> no existe
  // y tocar la instancia tira "Instance created by useForm is not connected to
  // any Form element". Recien cuando `modalVisible` pasa a true el Form esta
  // montado. Mismo criterio que en la ficha del micrositio.
  useEffect(() => {
    if (modalVisible && ofertaActiva) {
      form.setFieldsValue({ monto: minimoDe(ofertaActiva) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalVisible, ofertaActiva?.id_oferta]);

  const handlePreOferta = async (values) => {
    setSubmitting(true);
    try {
      const res = await ofertaService.create({
        id_animal: ofertaActiva.id_animal,
        monto_oferta: values.monto,
      });
      if (res.data?.lidera === false) {
        // Entró, pero otra más alta se metió en el medio. Mismo criterio que la
        // ficha: se avisa y se ofrece el peldaño siguiente en vez de dejarla
        // creyendo que va ganando.
        message.warning(res.data.message || 'Tu oferta se registró, pero ya no es la más alta');
        ofrecerReoferta(res.data);
      } else {
        message.success('Pre-oferta registrada exitosamente');
        setModalVisible(false);
      }
      await fetchOfertas();
    } catch (err) {
      const data = err.response?.data || {};
      if (data.code === 'condiciones_pendientes') {
        // Mismo flujo que la ficha: se muestran las condiciones del remate y,
        // al aceptarlas, se reintenta sola la oferta que quedó trunca. Antes
        // esto era un callejón sin salida — desde el panel no había forma de
        // aceptar las condiciones y la persona quedaba con un toast rojo.
        setMontoPendiente(values.monto);
        try {
          const { data: cond } = await buyerService.getCondiciones(ofertaActiva.id_evento);
          setCondiciones(cond);
          setCondicionesVisible(true);
        } catch {
          message.error('No pudimos cargar las condiciones del remate');
        }
      } else if (data.sugerido != null) {
        // Quedarse corto porque otro ofertó más NO es un error de carga, es
        // "te superaron": va al reintento SIN el cartel rojo, igual que la
        // ficha. El toast iba antes de este check y salía en los dos casos.
        message.warning(data.error || 'Tu oferta ya no es la más alta');
        ofrecerReoferta(data);
        await fetchOfertas();
      } else {
        message.error(data.error || 'Error al registrar la oferta');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const aceptarYOfertar = async () => {
    setAceptando(true);
    const monto = montoPendiente;
    try {
      await buyerService.aceptarCondiciones(ofertaActiva.id_evento);
      setCondicionesVisible(false);
      // Se reintenta la MISMA oferta que quedó trunca. `montoPendiente` NO se
      // limpia acá: si el reintento vuelve a chocar con condiciones (otra
      // pestaña), el flujo tiene que poder repetirse.
      if (monto != null) await handlePreOferta({ monto });
    } catch (err) {
      message.error(err.response?.data?.error || 'No pudimos registrar la aceptación');
    } finally {
      setAceptando(false);
    }
  };

  /** Deja el modal abierto con el próximo monto ya cargado, listo para reintentar. */
  const ofrecerReoferta = (data) => {
    setOfertaActiva((prev) => (prev ? {
      ...prev,
      mejor_oferta: data.mejor_oferta ?? prev.mejor_oferta,
      es_lider: false,
    } : prev));
    if (data.sugerido != null) form.setFieldsValue({ monto: Number(data.sugerido) });
  };

  /**
   * Junta en UNA fila las ofertas que son la misma puja sobre un corral.
   *
   * Un corral agrupado se remata entero, así que cada oferta deja una fila por
   * animal: una puja sobre un corral de cuatro aparecía como cuatro renglones
   * idénticos. Se agrupan por corral + monto + fecha + hora (que es lo que las
   * hace ser la MISMA puja) y los animales quedan como hijos desplegables.
   *
   * Las ofertas de animales individuales no se tocan.
   */
  const agruparPorLote = (filas) => {
    const lotes = new Map();
    const salida = [];
    for (const r of filas) {
      if (!r.agrupado || r.corral_oferta == null) { salida.push(r); continue; }
      const clave = `${r.corral_oferta}|${r.monto_oferta}|${r.fecha_oferta}|${r.hora_oferta}`;
      const lote = lotes.get(clave);
      if (lote) { lote.children.push(r); continue; }
      const nuevo = { ...r, es_lote: true, children: [r] };
      lotes.set(clave, nuevo);
      salida.push(nuevo);
    }
    // Un "lote" de un solo animal no necesita desplegable: se muestra plano.
    return salida.map(r => (r.es_lote && r.children.length === 1
      ? { ...r.children[0] }
      : r));
  };

  const ofertasFiltradas = useMemo(() => {
    if (!busqueda.trim()) return agruparPorLote(ofertas);
    const q = busqueda.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    // Se agrupa DESPUES de filtrar: al reves, buscar por el nombre de un animal
    // escondido dentro de un lote no lo encontraria.
    return agruparPorLote(ofertas.filter((r) => {
      const nombre = (r.animal?.nombre || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const rp     = (r.animal?.rp    || '').toLowerCase();
      const corral = String(r.corral_oferta ?? '');
      const monto  = String(r.monto_oferta  ?? '');
      const fecha  = r.fecha_oferta ? dayjs(r.fecha_oferta).format('DD/MM/YYYY') : '';
      return nombre.includes(q) || rp.includes(q) || corral.includes(q)
          || monto.includes(q)  || fecha.includes(q);
    }));
  }, [ofertas, busqueda]);

  const columnas = [
    {
      title: 'Animal',
      key: 'animal',
      sorter: (a, b) => (a.animal?.nombre || '').localeCompare(b.animal?.nombre || ''),
      // La fila de un lote no nombra a un animal: la puja fue por el corral
      // entero. Los animales quedan adentro, en el desplegable.
      render: (_, r) => (r.es_lote
        ? <strong>{`Corral ${r.corral_oferta} — ${r.children.length} animales`}</strong>
        : (r.animal?.nombre || `Animal #${r.id_animal}`)),
    },
    {
      title: 'Corral',
      dataIndex: 'corral_oferta',
      key: 'corral',
      width: 80,
      align: 'center',
      sorter: (a, b) => (a.corral_oferta ?? 0) - (b.corral_oferta ?? 0),
      render: (v) => v ?? '—',
    },
    {
      title: 'RP',
      key: 'rp',
      width: 110,
      sorter: (a, b) => (a.animal?.rp || '').localeCompare(b.animal?.rp || ''),
      render: (_, r) => r.animal?.rp || '—',
    },
    {
      title: 'Evento',
      key: 'evento',
      sorter: (a, b) => (a.id_evento ?? 0) - (b.id_evento ?? 0),
      render: (_, r) => `#${r.id_evento}`,
    },
    {
      title: 'Monto',
      dataIndex: 'monto_oferta',
      key: 'monto',
      sorter: (a, b) => (a.monto_oferta ?? 0) - (b.monto_oferta ?? 0),
      render: (v) => (v ? `$${Number(v).toLocaleString('es-AR')}` : '—'),
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha_oferta',
      key: 'fecha',
      sorter: (a, b) => (a.fecha_oferta || '').localeCompare(b.fecha_oferta || ''),
      render: (v) => (v ? dayjs(v).format('DD/MM/YYYY') : '—'),
    },
    {
      title: 'Hora',
      dataIndex: 'hora_oferta',
      key: 'hora',
      width: 90,
      align: 'center',
      sorter: (a, b) => (a.hora_oferta || '').localeCompare(b.hora_oferta || ''),
      render: (v) => (v ? v.slice(0, 5) : '—'),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      align: 'center',
      width: 140,
      render: (_, r) => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
          <Tooltip title={r.es_lider ? 'Oferta líder' : 'Oferta superada'}>
            {r.es_lider
              ? <ArrowUpOutlined style={{ color: '#52c41a', fontSize: 16 }} />
              : <ArrowDownOutlined style={{ color: '#f5222d', fontSize: 16 }} />
            }
          </Tooltip>

          {/* El botón se APAGA si la ventana no está abierta, en vez de dejar
              tipear un monto y comerse un 400. El estado sale del mismo
              `estado_ofertas()` que decide el candado en el backend. */}
          <Tooltip title={motivoNoOfertable(r) || 'Nueva pre-oferta'}>
            <Button
              type="text"
              size="small"
              disabled={!!motivoNoOfertable(r)}
              icon={<DollarOutlined style={{ color: motivoNoOfertable(r) ? '#bbb' : '#1890ff' }} />}
              onClick={() => abrirModalPreOferta(r)}
            />
          </Tooltip>

          {r.evento_slug && r.animal?.id && r.animal?.nombre && (
            <Tooltip title="Ver animal">
              <Button
                type="text"
                size="small"
                icon={<SearchOutlined />}
                // Mismo criterio que en Remates: con el micrositio incrustado,
                // la ficha vive en el dominio del productor.
                href={urlMicrositio({
                  config: r,
                  eventoSlug: r.evento_slug,
                  animal: { id: r.animal.id, nombre: r.animal.nombre },
                })}
                target="_blank"
                rel="noopener noreferrer"
              />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  if (loading) {
    return <PujaSpinner />;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Mis Ofertas</h2>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Buscar por animal, RP, corral, monto o fecha..."
          allowClear
          style={{ width: 320 }}
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
      </div>

      <Table
        scroll={{ x: 'max-content' }}
        // La fila de un lote es una COPIA de su primera oferta, asi que padre e
        // hijo comparten `id_oferta` y con `rowKey="id_oferta"` React veia dos
        // filas con la misma key. El prefijo las separa.
        rowKey={(r) => (r.es_lote ? `lote-${r.id_oferta}` : r.id_oferta)}
        dataSource={ofertasFiltradas}
        columns={columnas}
        pagination={{ pageSize: 20 }}
        showSorterTooltip={false}
      />

      <Modal
        title={`Nueva pre-oferta — ${ofertaActiva?.animal?.nombre || ''}`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnHidden
      >
        {/* Estado de la puja, como en la ficha: la persona tiene que saber
            contra qué está ofertando ANTES de tipear. */}
        {ofertaActiva?.mejor_oferta != null && (
          <div style={{ marginBottom: 14, fontSize: 13, color: '#555' }}>
            Última oferta: <strong>${Number(ofertaActiva.mejor_oferta).toLocaleString('es-AR')}</strong>
            {ofertaActiva.es_lider && (
              <span style={{ color: '#389e0d', fontWeight: 600 }}> — la tuya, estás liderando</span>
            )}
            {ofertaActiva.salto_oferta ? (
              <div style={{ color: '#888', marginTop: 2 }}>
                Salto de oferta: ${Number(ofertaActiva.salto_oferta).toLocaleString('es-AR')}
              </div>
            ) : null}
          </div>
        )}

        {ofertaActiva?.es_lider && (
          // Mismo aviso que la ficha: si ya lidera, la nueva oferta no compite
          // contra nadie y le sube el precio contra sí misma.
          <div style={{
            marginBottom: 14, padding: '8px 12px', borderRadius: 8,
            background: '#f6ffed', border: '1px solid #b7eb8f',
            fontSize: 12.5, color: '#254a12',
          }}>
            <strong>Ya estás liderando.</strong> Si ofertás de nuevo vas a superar tu propia oferta.
          </div>
        )}

        <Form form={form} layout="vertical" onFinish={handlePreOferta}>
          <Form.Item
            name="monto"
            label="Monto de la oferta"
            rules={[
              { required: true, message: 'Ingresá el monto' },
              // El mínimo y el escalón se validan acá con las MISMAS cuentas
              // que el backend, para no mandar algo que va a rebotar.
              () => ({
                validator(_, value) {
                  if (value == null) return Promise.resolve();
                  const minimo = minimoDe(ofertaActiva);
                  if (Number(value) < minimo) {
                    return Promise.reject(new Error(
                      `La oferta mínima es $${minimo.toLocaleString('es-AR')}`));
                  }
                  const salto = Number(ofertaActiva?.salto_oferta || 0);
                  if (salto > 0 && !enEscalon(value, ofertaActiva?.precio_base, salto)) {
                    const proximo  = escalonSiguiente(value, ofertaActiva?.precio_base, salto);
                    const anterior = proximo - salto;
                    const opciones = anterior >= minimo
                      ? `$${anterior.toLocaleString('es-AR')} o $${proximo.toLocaleString('es-AR')}`
                      : `$${Math.max(proximo, minimo).toLocaleString('es-AR')}`;
                    return Promise.reject(new Error(
                      `Las ofertas suben de a $${salto.toLocaleString('es-AR')}. Podés ofertar ${opciones}`));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputNumber
              prefix="$"
              style={{ width: '100%' }}
              min={minimoDe(ofertaActiva)}
              step={Number(ofertaActiva?.salto_oferta) || 1000}
              // Compartidos con la ficha del micrositio: la version inline
              // multiplicaba por cien cualquier monto con decimales.
              formatter={formatearMonto}
              parser={parsearMonto}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={() => setModalVisible(false)} style={{ marginRight: 8 }}>
              Cancelar
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Confirmar oferta
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Condiciones DEL REMATE. Se aceptan una sola vez por evento y el backend
          las exige recién al ofertar. Sin este modal, ofertar desde el panel era
          un callejón sin salida: el 403 salía como toast y no había forma de
          aceptarlas. Mismo flujo que la ficha del micrositio. */}
      <Modal
        open={condicionesVisible}
        onCancel={() => { setCondicionesVisible(false); setMontoPendiente(null); }}
        onOk={aceptarYOfertar}
        okText="Acepto y ofertar"
        cancelText="Cancelar"
        confirmLoading={aceptando}
        width={720}
        title="Condiciones del remate"
      >
        <p style={{ fontSize: 13, color: '#666' }}>
          Antes de tu primera oferta en este remate necesitamos que aceptes las
          condiciones que fija la cabaña.
        </p>
        {condiciones?.condiciones ? (
          <div style={{ maxHeight: '50vh', overflowY: 'auto', fontSize: 14 }}>
            {[
              ['titulo_comercial', 'condiciones_comerciales'],
              ['titulo_sanitaria', 'condiciones_sanitarias'],
              ['titulo_reclamos',  'reclamos_garantias'],
            ].map(([kTitulo, kCuerpo]) => (
              condiciones.condiciones[kCuerpo] ? (
                <section key={kCuerpo} style={{ marginBottom: 20 }}>
                  <h4 style={{ marginBottom: 6 }}>
                    {condiciones.condiciones[kTitulo] || 'Condiciones'}
                  </h4>
                  {/* HTML cargado por la propia cabaña desde su panel, igual
                      que en el micrositio — y sanitizado igual que allá. */}
                  <div {...htmlSanitizado(condiciones.condiciones[kCuerpo])} />
                </section>
              ) : null
            ))}
          </div>
        ) : (
          <PujaSpinner />
        )}
      </Modal>
    </div>
  );
};

export default MisOfertas;
